import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from '../../auth/auth.service';
import { Permission, Role, Roles, User } from '../../interfaces/permission';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { CenterCode, CenterData } from '../../interfaces/center';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-usermenu',
  imports: [MatIconModule, MatMenuModule, MatFormFieldModule, CommonModule, MatSelectModule, MatOptionModule, CommonModule, FormsModule, MatButtonModule],
  templateUrl: './usermenu.component.html',
  styleUrl: './usermenu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UsermenuComponent {
  allRoles = Object.values(Roles);
  centers = Object.entries(CenterData);
  currentRole: Role | null = null;
  currentUser: User | null = null;

  switchRoleSelection!: Role;
  switchCenterSelection?: CenterCode;
  constructor(
    readonly authService: AuthService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.matIconRegistry.addSvgIcon('human-male', this.domSanitizer.bypassSecurityTrustResourceUrl('/human-male.svg'));
    this.loadCurrentRole();
    this.getUser();
  }

  async getUser() {
    //return this.authService.roleNames[user.role as Role] + (user.role === 'center' ? ' ' + user.center : '');
    this.currentUser = await firstValueFrom(this.authService.getUser());
    this.cdr.markForCheck();
  }

  async loadCurrentRole() {
    this.currentRole = await this.authService.getCurrentRole();
    this.cdr.markForCheck();
  }

  /**
   * Get available roles for role switching.
   * OPERATOR can switch to any role, non-OPERATOR can only switch to their own roles.
   */
  get availableRoles(): Role[] {
    if (!this.currentRole || !this.currentUser) {
      return [];
    }

    // Check if user is OPERATOR (can switch to any role)
    const isOperator = this.currentRole.permissions.includes(Permission.SWITCH_ROLE);

    if (isOperator) {
      return this.allRoles; // OPERATOR can switch to any role
    } else {
      return this.currentUser.roles; // Non-OPERATOR can only switch to their own roles
    }
  }

  /**
   * Get available centers for center selection.
   * OPERATOR can select any center, non-OPERATOR can only select their assigned centers.
   */
  get availableCenters(): [string, any][] {
    if (!this.currentUser) {
      return [];
    }

    // Check if user is OPERATOR (can access any center)
    const isOperator = this.currentRole?.permissions.includes(Permission.SWITCH_ROLE) ?? false;

    if (isOperator) {
      return this.centers; // OPERATOR can access any center
    } else {
      // Filter centers to only show those the user has access to
      return this.centers.filter(([centerCode]) => this.currentUser?.centerNames?.includes(centerCode));
    }
  }

  /**
   * Check if user is OPERATOR (can switch to any role)
   */
  get isOperator(): boolean {
    return this.currentRole?.permissions.includes(Permission.SWITCH_ROLE) ?? false;
  }

  /**
   * Check if user can switch roles (has more than one role OR is OPERATOR)
   */
  get canSwitchRoles(): boolean {
    return this.isOperator || (this.currentUser?.roles?.length ?? 0) > 1;
  }

  async switchRole(role: Role, centerCode?: CenterCode) {
    if (role.name === 'center' && !centerCode) return; // Don't switch until center is selected
    this.authService.setSwitchedRole(role, centerCode ?? '').subscribe({
      next: () => {
        localStorage.setItem('switchedRole', 'true');
        window.location.reload();
      },
      error: (err) => console.error(err)
    });
  }

  clearSwitchedRole() {
    this.authService.clearSwitchedRole().subscribe({
      next: () => {
        console.log('Switched role cleared successfully');
        localStorage.removeItem('switchedRole');

        window.location.reload();
      },
      error: (err) => console.error(err)
    });
  }

  //Used to show avatars. We can implement this by using user.email however this doesn't work yet and is more a nice to ahve.
  avatarUrl() {
    return `//www.gravatar.com/avatar/email?d=mm`;
  }

  changeAvatarUrl() {
    return `//www.gravatar.com/profiles/email?d=mm`;
  }

  hasSwitchedRole(): boolean {
    return !!localStorage.getItem('switchedRole');
  }
}
